import { type CSSProperties, useEffect, useRef, useState } from 'react';

import { cn } from '../../utils/cn';
import { getHeightStyle } from '../../utils/sizing';

import * as styles from '../Navbar.css';
import type { NavbarMenuListGroupElementProps } from '../Navbar.type';
import { useNavbarMenuListContext } from './useNavbarMenuListContext';

const DEFAULTS = {
  minHeight: '28px',
};

export function NavbarMenuListGroup(props: NavbarMenuListGroupElementProps) {
  const { element, className, children, style, screen, attributes } = props;
  const { type, customAttributes } = element.attrs || {};

  const middleWrapperRef = useRef<HTMLDivElement>(null);
  const { middleWrapperHeight, setMiddleWrapperHeight } = useNavbarMenuListContext();

  const middleOuterRef = useRef<HTMLDivElement>(null);
  const [maxWidth, setMaxWidth] = useState<number | null>(null);

  // Observe content height changes
  useEffect(() => {
    if (!middleWrapperRef.current || type !== 'middle') return;

    const updateHeight = () => {
      if (middleWrapperRef.current) {
        setMiddleWrapperHeight(middleWrapperRef.current.clientHeight);
      }
    };

    // Create observer to watch for height changes in children
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(middleWrapperRef.current);

    // Initial height calculation
    updateHeight();

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Observe content width changes
  useEffect(() => {
    if (!middleOuterRef.current || type !== 'middle') return;
    const leftSibling = middleOuterRef.current?.parentElement?.firstChild as HTMLDivElement;
    const rightSibling = middleOuterRef.current?.parentElement?.lastChild as HTMLDivElement;
    const parentElement = middleOuterRef.current?.parentElement;

    const updateMaxWidth = () => {
      const biggestSiblingWidth = Math.max(leftSibling?.scrollWidth || 0, rightSibling?.scrollWidth || 0);
      const parentWidth = parentElement?.scrollWidth || 0;
      setMaxWidth(parentWidth - biggestSiblingWidth * 2);
    };

    // Create observers for left and right siblings
    const resizeObserver = new ResizeObserver(updateMaxWidth);
    if (leftSibling) resizeObserver.observe(leftSibling);
    if (rightSibling) resizeObserver.observe(rightSibling);
    if (parentElement) resizeObserver.observe(parentElement);

    // Initial max width calculation
    updateMaxWidth();

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (type === 'left') {
    return (
      <div
        {...props}
        {...attributes}
        {...customAttributes}
        className={cn(styles.navbarMenuListGroup, styles.alignLeft, className)}
        style={
          {
            '--min-height': middleWrapperHeight ? getHeightStyle(middleWrapperHeight).height : DEFAULTS.minHeight,
            ...style,
          } as CSSProperties
        }
      >
        {children}
      </div>
    );
  }

  if (type === 'middle') {
    return (
      <>
        {/* This centerPlaceholder is required to make sure the middle doesn't collapse to 0 width when width is `fit-content` */}
        {screen !== 'mobile' && (
          <div
            className={styles.centerPlaceholder}
            style={{ '--center-placeholder-width': maxWidth ? `${maxWidth}px` : '100%' } as CSSProperties}
          />
        )}
        <div
          ref={middleOuterRef}
          {...attributes}
          className={cn(styles.centerWrapper)}
          style={{ ...style, '--max-width': maxWidth ? `${maxWidth}px` : '100%' } as CSSProperties}
        >
          <div
            {...props}
            {...customAttributes}
            className={cn(styles.navbarMenuListGroup, styles.alignCenter, className)}
            ref={middleWrapperRef}
          >
            {children}
          </div>
        </div>
      </>
    );
  }

  return (
    <div
      {...props}
      {...attributes}
      {...customAttributes}
      className={cn(styles.navbarMenuListGroup, styles.alignRight, className)}
      style={
        {
          '--min-height': middleWrapperHeight ? getHeightStyle(middleWrapperHeight).height : DEFAULTS.minHeight,
          ...style,
        } as CSSProperties
      }
    >
      {children}
    </div>
  );
}
